/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChatChannel,
  IChatConversation,
  IChatMessage,
  IChatParticipant,
  SendStatus,
} from '@dataTypes/chat.type'
import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import {
  addConversation,
  addChat,
  bulkDeleteConversationsById,
  bulkUpdateChatParticipants,
  ConversationResponseData,
  deleteChatParticipantById,
  getAllChatConversation,
  getBulkPresignedUrl,
  markConversationAsRead,
  updateConversationById,
  updateChatById,
} from '@services/api/chat.api'
import { logger } from '@utils/safeLogger'
import { sortConversationByDate } from '@utils/chat.util'
import { IFavoritedUser } from '@dataTypes/user-favorite.type'
import {
  getAllUsers,
  getFavoritedUsers,
  deleteFavoritedUsersByIds,
  createFavoritedUsers,
} from '@services/api/user-favorites.api'

export const receiveInternalChatMessage = createAction<{
  message: IChatMessage
}>('api/chat/internal/receiveMessage')

export const internalChatParticipantRemoved = createAction<{
  chat_participant_id: string
}>('api/chat/internal/participantRemoved')

export const markInternalConversationAsRead = createAsyncThunk<
  { chat_conversation_id: string; last_read_at: string },
  string,
  { rejectValue: string }
>(
  'api/chat/internal/markAsRead',
  async (chat_conversation_id, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const user_id = user.user_id
      if (!user_id) return rejectWithValue('User not found')
      const response = await markConversationAsRead(
        chat_conversation_id,
        user_id,
      )
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message || 'Failed to mark as read')
      }
      const last_read_at = new Date().toISOString()
      return { chat_conversation_id, last_read_at }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to mark as read')
    }
  },
)

export const fetchAllInternalChats = createAsyncThunk<
  IChatConversation[],
  void,
  { rejectValue: string }
>('api/chat/internal/fetch', async (_, { rejectWithValue }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const response = await getAllChatConversation('Internal', user.user_id)

  if (response.statusCode !== 200) {
    logger.error('Failed to fetch internal chats', {
      status: response.statusCode,
      message: response.message,
    })
    return rejectWithValue(response.message)
  }

  if (
    response.data &&
    'conversations' in response.data &&
    Array.isArray(response.data.conversations)
  ) {
    const _conversations = response.data.conversations

    if (_conversations.length < 1) {
      return []
    }

    const presignedResponse = await getBulkPresignedUrl(_conversations)

    if (presignedResponse.statusCode !== 200) {
      logger.error('Failed to get presigned URLs', {
        message: presignedResponse.message,
      })
      return rejectWithValue(presignedResponse.message)
    }

    if (!presignedResponse.data?.conversations) {
      logger.error('No conversations in presigned response')
      return rejectWithValue('No conversations in presigned response')
    }

    const conversations = presignedResponse.data
      .conversations as IChatConversation[]

    const sortedConversations = sortConversationByDate(conversations)

    return sortedConversations
  }

  logger.error('Invalid internal chat response format')
  return rejectWithValue('Conversations not found in the response')
})

export const bulkUpdateChatParticipantsAction = createAsyncThunk<
  { ids: string[]; data: Partial<IChatParticipant> },
  { ids: string[]; data: Partial<IChatParticipant> },
  { rejectValue: string }
>(
  'api/chat/internal/bulkUpdate/participants',
  async ({ ids, data }, { rejectWithValue }) => {
    try {
      const response = await bulkUpdateChatParticipants(ids, data)
      if (response.statusCode !== 200) {
        return rejectWithValue(
          response.message || 'Failed to bulk update participants',
        )
      }
      return { ids, data }
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to bulk update participants',
      )
    }
  },
)

export const deleteChatParticipant = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  'api/chat/internal/delete/participant',
  async (chatParticipantId, { rejectWithValue }) => {
    try {
      let participantId: string = chatParticipantId
      if (!participantId || participantId.trim() === '') {
        const user = JSON.parse(localStorage.getItem('user') || '{}')
        participantId = user.user_id
      }
      const response = await deleteChatParticipantById(participantId)
      if (response.statusCode !== 200) {
        return rejectWithValue(
          response.message || 'Failed to remove participant',
        )
      }
      return chatParticipantId
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to remove participant')
    }
  },
)

export const createInternalConversation = createAsyncThunk<
  ConversationResponseData,
  IChatParticipant[],
  { rejectValue: string }
>(
  'api/chat/internal/add/conversation',
  async (chat_participants, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const participants: IChatParticipant[] = chat_participants.map((p) => ({
        ...p,
        chat_conversation_id: '',
        chat_participant_id: undefined,
      }))
      participants.push({
        chat_conversation_id: '',
        user_id: user.user_id,
        username: user.username,
      })
      const response = await addConversation(
        {
          channel: ChatChannel.inApp,
          chat_type: 'Internal',
          chat_message: [],
          chat_participant: participants,
        },
        participants,
      )
      return response.data as ConversationResponseData
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create conversation')
    }
  },
)

export const updateInternalConversation = createAsyncThunk<
  { id: string; data: Partial<IChatConversation> },
  { id: string; data: Partial<IChatConversation> },
  { rejectValue: string }
>(
  'api/chat/internal/update/conversation',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await updateConversationById(id, data)
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }
      return { id, data }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update conversation')
    }
  },
)

export const updateInternalChat = createAsyncThunk<
  { chat_message_id: string; data: Partial<IChatMessage> },
  { chat_message_id: string; data: Partial<IChatMessage> },
  { rejectValue: string }
>('api/chat/update/chat', async (args, { rejectWithValue }) => {
  try {
    await updateChatById(args.chat_message_id, args.data)
    return args
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update chat')
  }
})

export const bulkDeleteConversations = createAsyncThunk<
  string[],
  string[],
  { rejectValue: string }
>(
  'api/chat/internal/delete/conversation/bulk',
  async (chatConversationIds, { rejectWithValue }) => {
    try {
      if (!chatConversationIds?.length) return []
      const response = await bulkDeleteConversationsById(chatConversationIds)
      if (response.statusCode !== 200) {
        return rejectWithValue(
          response.message || 'Failed to delete conversations',
        )
      }
      return response.data?.deleted_ids ?? chatConversationIds
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete conversations')
    }
  },
)

export const addInternalChatMessage = createAsyncThunk<
  IChatMessage,
  IChatMessage,
  { rejectValue: string }
>('api/chat/internal/add/message', async (message, { rejectWithValue }) => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    const payload: IChatMessage = {
      ...message,
      channel: message.channel ?? ChatChannel.inApp,
      sender_id: message.sender_id.length ? message.sender_id : user.user_id,
    }
    const response = await addChat(payload)
    if (response.statusCode !== 201) {
      return rejectWithValue(response.message || 'Failed to send message')
    }

    // Use saved message from API (has chat_message_id and send_status 'sent' from DB) or fallback to input with sent status
    const savedChat = response.data?.chat
    if (savedChat) {
      return {
        ...message,
        ...savedChat,
        send_status: SendStatus.sent,
      } as IChatMessage
    }
    return { ...message, send_status: SendStatus.sent } as IChatMessage
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to send message')
  }
})

// Favorite Users
export const fetchAllUsers = createAsyncThunk<
  IFavoritedUser[],
  void,
  { rejectValue: string }
>('api/user-favorites/fetchAllUsers', async (_, { rejectWithValue }) => {
  try {
    const response = await getAllUsers()
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    if (
      response.data &&
      'users' in response.data &&
      Array.isArray(response.data.users)
    ) {
      return response.data.users as IFavoritedUser[]
    }
    return []
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch all users')
  }
})

export const fetchAllFavoritedUsers = createAsyncThunk<
  IFavoritedUser[],
  void,
  { rejectValue: string }
>('api/user-favorites/fetch', async (_, { rejectWithValue }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  try {
    const response = await getFavoritedUsers(user.user_id)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    if (
      response.data &&
      'favorited_users' in response.data &&
      Array.isArray(response.data.favorited_users)
    ) {
      return response.data.favorited_users as IFavoritedUser[]
    }
    return []
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch favorited users')
  }
})

export const addFavoritedUsers = createAsyncThunk<
  IFavoritedUser[],
  string[],
  { rejectValue: string }
>('api/user-favorites/add', async (favoritedUserIds, { rejectWithValue }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  try {
    const response = await createFavoritedUsers({
      user_id: user.user_id,
      favorited_user_ids: favoritedUserIds,
    })
    if (response.statusCode !== 200) {
      return rejectWithValue(
        response.message || 'Failed to set favorited users',
      )
    }
    if (
      response.data &&
      'favorited_users' in response.data &&
      Array.isArray(response.data.favorited_users)
    ) {
      return response.data.favorited_users as IFavoritedUser[]
    }
    return []
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to set favorited users')
  }
})

export const bulkDeleteUsers = createAsyncThunk<
  string[],
  string[],
  { rejectValue: string }
>(
  'api/user-favorites/delete',
  async (favoritedUserIds, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await deleteFavoritedUsersByIds({
        user_id: user.user_id,
        favorited_user_ids: favoritedUserIds,
      })
      if (response.statusCode !== 200) {
        return rejectWithValue(
          response.message || 'Failed to remove favorited users',
        )
      }
      return favoritedUserIds
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to remove favorited users',
      )
    }
  },
)
